import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  IconButton,
  Breadcrumbs,
  ListItemIcon,
  LinearProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  CardMedia,
  CardContent,
  Grid,
  Card,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import { Delete, Edit, Upload, Assignment, PlayCircleFilledRounded, MoreVert } from "@mui/icons-material";
import FolderIcon from '@mui/icons-material/Folder';
import { db, storage } from "../../firebaseConfig";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import {
  ref,
  deleteObject,
  listAll
} from "firebase/storage";
import VideoDialog from "../../common/VideoDialog";
import { uploadVideo } from "../../common/VideoUploader";
import ConfirmDialog from "../../utils/ConfirmDialog";
import ProgressBar from "../../common/ProgreeBar";
import Loader from "../../common/Loader";
import { formatDuration, formatRelativeDate } from "../../utils/DateTimeUtils";
import ThumbnailUploadModal from "../../common/ThumbnailUploadModal";
import { ThumbnailUploader } from "../../common/ThumbnailUploader";

/**  Recursively returns *all* descendant document‑refs
 *   starting at videos/.../items/<folderId>  (or a single video)
 */
const getDescendantItemRefs = async (db, startCollectionPath) => {
  const toVisit = [startCollectionPath]; // e.g., videos/Java/items/1748158418523/items
  const allRefs = [];

  while (toVisit.length) {
    const currentCollectionPath = toVisit.pop();

    const snap = await getDocs(collection(db, currentCollectionPath));
    for (const docSnap of snap.docs) {
      const docId = docSnap.id; // ✅ Use the actual Firestore document ID
      const docRef = doc(db, currentCollectionPath, docId); // ✅ Correct reference
      allRefs.push(docRef);

      // Recurse into this document’s items subcollection
      const nextCollectionPath = `${currentCollectionPath}/${docId}/items`;
      toVisit.push(nextCollectionPath);
    }
  }

  return allRefs;
};



const VideosDashboard = () => {
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [newFolderName, setNewFolderName] = useState("");
  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deletedItem, setDeletedItem] = useState(null);
  const [renamedPath, setRenamedPath] = useState([]);
  const [loading, setLoading] = useState(false);

  // New state for assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignItem, setAssignItem] = useState(null);
  const [students, setStudents] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignedStudentIds, setAssignedStudentIds] = useState(new Set());
  const [loadingDeletion, setLoadingDeletion] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);

  const pathString = currentPath.join("/items/");

  useEffect(() => {
    fetchFolders();
  }, [currentPath, refresh]);

  // Fetch all folders/videos in the current path
  const fetchFolders = async () => {
    setLoading(true);
    try {
      const folderRef = collection(db, `videos/${pathString || "root"}/items`);
      const snapshot = await getDocs(folderRef);
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setFolders(data);
    } catch (error) {
      console.error("Failed to fetch:", error);
      setSnackbar({ open: true, message: "Failed to load Files and Folders", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all students for the assignment popup
  const fetchStudents = async () => {
    setAssignLoading(true);
    try {
      const studentsRef = collection(db, "users");
      const snapshot = await getDocs(studentsRef);
      const studentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(studentsData);
    } catch (error) {
      console.error("Failed to fetch students:", error);
      setSnackbar({ open: true, message: "Failed to load students", severity: "error" });
    }
    setAssignLoading(false);
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      alert("Folder name cannot be empty");
      return;
    }
    const id = Date.now().toString();
    await setDoc(doc(db, `videos/${pathString || "root"}/items`, id), {
      type: "folder",
      name: newFolderName,
      assignedStudents: [],  // initialize empty assignments
    });
    setOpenFolderDialog(false);
    setNewFolderName("");
    setRefresh(!refresh);
  };

  const handleUpload = async () => {
    uploadVideo({
      selectedFile,
      pathString,
      onProgress: (p) => setProgress(p),
      onComplete: (url, name) => {
        console.log("Upload completed:");
        setSelectedFile(null);
        setProgress(0);
        setRefresh(!refresh);
      },
      onError: (error) => {
        alert("Failed to upload: " + error.message);
      },
    });
  };

  const handleDelete = async (item) => {
    setConfirmOpen(true);
    setDeletedItem(item);
  };

  const handleRename = async (item, newName) => {
    const itemRef = doc(db, `videos/${pathString || "root"}/items`, item.id);
    await updateDoc(itemRef, { displayName: newName });
    setRefresh(!refresh);
  };

  const handleVideoClick = (url) => {
    setSelectedVideoUrl(url);
    setOpenVideoDialog(true);
  };

  const handleConfirmClose = useCallback(() => {
    setConfirmOpen(false);
    setSelectedFile(null);
  }, []);

  // Deleting files and folders in firebase storage
  const deleteStorageFolderRecursively = async (folderPath) => {
    const folderRef = ref(storage, folderPath);

    try {
      const listResult = await listAll(folderRef);

      // Recursively delete all subfolders first
      for (const prefixRef of listResult.prefixes) {
        await deleteStorageFolderRecursively(prefixRef.fullPath);
      }

      // Then delete all files in the current folder
      for (const fileRef of listResult.items) {
        await deleteObject(fileRef);
      }
    } catch (error) {
      console.error(`Failed to delete from storage path: ${folderPath}`, error);
    }
  };

  const deleteFolderRecursively = async (storagefolderPath, folderPath) => {
    const filteredStoragefolderPath = storagefolderPath.replace(/^root\/items\/?/, "");
    if (!folderPath || !filteredStoragefolderPath) return;


    console.log("Deleting storage folder:", filteredStoragefolderPath);
    await deleteStorageFolderRecursively(`/${filteredStoragefolderPath}`);

    // Build Firestore path like: videos/Java/items/Chapter 1/items/...
    const segments = folderPath.split("/").filter(segment => segment && segment !== "items");
    let firestorePath = "videos";
    segments.forEach((segment, index) => {
      firestorePath += `/${segment}`;
      if (index !== segments.length - 1) {
        firestorePath += "/items";
      }
    });

    console.log("Firestore path:", firestorePath);
    const itemsCollectionRef = collection(db, `${firestorePath}/items`);
    const snapshot = await getDocs(itemsCollectionRef);

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (data.type === "folder") {
        await deleteFolderRecursively(
          `${storagefolderPath}/items/${data.name}`,
          `${folderPath}/${data.name}`
        );
      }

      await deleteDoc(doc(itemsCollectionRef, docSnap.id));
    }
  };



  const confirmDeleteVideo = async () => {
    setLoadingDeletion(true)
    try {
      const firestorePath = pathString || "root";

      if (deletedItem.type === "video") {
        // Delete Firestore doc
        await deleteDoc(doc(db, `videos/${firestorePath}/items`, deletedItem.id));

        // Delete from Storage
        const storageRef = ref(storage, `${firestorePath}/${deletedItem.name}`);
        await deleteObject(storageRef);
      } else if (deletedItem.type === "folder") {
        // Recursively delete folder contents (storage + nested firestore)
        await deleteFolderRecursively(
          `${firestorePath}/items/${deletedItem.name}`, // storage path
          `${pathString}/${deletedItem.name}`                         // firestore folder root
        );

        // Now delete the doc reference itself
        await deleteDoc(doc(db, `videos/${firestorePath}/items`, deletedItem.id));
      }

      setRefresh(!refresh);
      setSelectedFile(null);
      setSnackbar({
        open: true,
        message: `${capitalizeFirstLetter(deletedItem?.type)} Deleted successfully!`,
        severity: "success",
      });
    } catch (error) {
      console.error(`Error deleting ${capitalizeFirstLetter(deletedItem?.type)}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to delete ${capitalizeFirstLetter(deletedItem?.type)}!`,
        severity: "error",
      });
    } finally {
      handleConfirmClose();
      setLoadingDeletion(false)
    }
  };


  function capitalizeFirstLetter(word) {
    if (!word) return "";
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  // ** ASSIGN HANDLERS **

  const openAssignDialog = async (item) => {
    setAssignItem(item);
    setAssignDialogOpen(true);
    await fetchStudents();

    // Set assigned student IDs for that item (if any)
    const assigned = new Set(item.assignedStudents || []);
    setAssignedStudentIds(assigned);
  };

  const handleAssignCheckboxChange = (studentId) => {
    const newSet = new Set(assignedStudentIds);
    if (newSet.has(studentId)) {
      newSet.delete(studentId);
    } else {
      newSet.add(studentId);
    }
    setAssignedStudentIds(newSet);
  };

  const handleAssignConfirm = async () => {
    if (!assignItem) return;
    setAssigning(true);

    try {
      const assignedArr = Array.from(assignedStudentIds);
      const firestorePath = pathString || "root"; // e.g., Java or root

      // Start recursive assignment
      const assignFolderRecursively = async (folderPath) => {
        const segments = folderPath.split("/").filter(segment => segment && segment !== "items");
        let basePath = "videos";
        segments.forEach((segment, index) => {
          basePath += `/${segment}`;
          if (index !== segments.length - 1) {
            basePath += "/items";
          }
        });

        const itemsCollectionRef = collection(db, `${basePath}/items`);
        const snapshot = await getDocs(itemsCollectionRef);

        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          if (!data.name) continue; // ✅ Only assign if "name" field exists

          const docRef = doc(itemsCollectionRef, docSnap.id);

          // ✅ Update current item with assignedStudents
          await setDoc(docRef, { assignedStudents: assignedArr }, { merge: true });

          if (data.type === "folder") {
            // Recurse into subfolder
            await assignFolderRecursively(`${folderPath}/${data.name}`);
          }
        }
      };

      // ✅ Assign to the main folder or video itself (only if it has a name)
      if (assignItem.name) {
        const mainDocRef = doc(db, `videos/${firestorePath}/items/${assignItem.id}`);
        await setDoc(mainDocRef, { assignedStudents: assignedArr }, { merge: true });

        if (assignItem.type === "folder") {
          // ✅ Start recursive assignment for children
          await assignFolderRecursively(`${pathString}/${assignItem.name}`);
        }
      }

      setSnackbar({ open: true, message: "Assigned to folder and its contents!", severity: "success" });
      setAssignDialogOpen(false);
      setAssignItem(null);
      setRefresh(prev => !prev);
    } catch (err) {
      console.error("Failed to assign:", err);
      setSnackbar({ open: true, message: "Assignment failed", severity: "error" });
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setAssignItem(null);
  };

  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedItem(null);
  };


  return (
    <>
      {loadingDeletion ? (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          display: "flex", justifyContent: "center", alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.2)", zIndex: 9999
        }}>
          <CircularProgress />
        </div>
      ) :
        <Box p={3}>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
          <Breadcrumbs>
            <Typography onClick={() => {
              setCurrentPath([])
              setRenamedPath([])
            }} sx={{ cursor: "pointer" }}>
              Root
            </Typography>
            {currentPath.map((p, idx) => (
              <Typography
                key={idx}
                onClick={() => {
                  setCurrentPath(currentPath.slice(0, idx + 1))
                  setRenamedPath(renamedPath.slice(0, idx + 1))
                }}
                sx={{ cursor: "pointer" }}
              >
                {renamedPath[idx] || p}
              </Typography>
            ))}
          </Breadcrumbs>

          <Box my={2} display="flex" justifyContent="space-between" alignItems="center">
            <h3 style={{ color: '#9c27b0', margin: 0 }}>All Videos</h3>
            <div style={{ margin: 0 }}>
              <Button variant="contained" onClick={() => setOpenFolderDialog(true)}>
                Create Folder
              </Button>
              {currentPath?.length !== 0 && <>
                <Button variant="contained" component="label" sx={{ ml: 2 }}>
                  <Upload /> Upload Video
                  <input
                    hidden
                    type="file"
                    accept="video/*"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                </Button>
                {progress === 0 && <Button
                  color="error"
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!selectedFile}
                  sx={{ ml: 2 }}
                >
                  Confirm Upload
                </Button>}
              </>}
            </div>
          </Box>

          {loading ? <Loader /> :
            <>
              {/* No content available message */}
              {folders.length === 0 && progress === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                  minHeight="300px" // You can adjust this as per your layout
                >
                  <Typography variant="h6" color="textSecondary">
                    No content available
                  </Typography>
                </Box>
              )}

              {/* Folders Section */}
              <List>
                {folders.filter((item) => item.type === "folder").map((item) => (
                  <ListItem
                    key={item.id}
                    secondaryAction={
                      <>
                        <IconButton onClick={() => handleDelete(item)} color="error">
                          <Delete />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            const newName = prompt("Enter new name", item.displayName ? item.displayName : item.name);
                            if (newName) handleRename(item, newName);
                          }}
                          color="secondary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => openAssignDialog(item)}
                          color="primary"
                          title="Assign to Students"
                        >
                          <Assignment />
                        </IconButton>
                      </>
                    }
                  >
                    <Box
                      sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                      onClick={() => {
                        setRenamedPath([...renamedPath, item.displayName ? item.displayName : item.name])
                        setCurrentPath([...currentPath, item.name])
                      }
                      }
                    >
                      <ListItemIcon>
                        <FolderIcon color="primary" />
                      </ListItemIcon>
                      <Typography ml={1}>{item.displayName ? item.displayName : item.name}</Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>

              {/* Divider */}
              {folders.some(item => item.type === 'folder') && folders.some(item => item.type === 'video') && (
                <Divider sx={{ my: 2, borderColor: 'grey.400' }}>
                  <Typography variant="caption" color="text.secondary">Videos</Typography>
                </Divider>
              )}

              {/* Videos Grid Section */}
              <Grid container spacing={2}>
                {folders.filter((item) => item.type === "video").map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.id} sx={{ marginBottom: 3 }}>
                    <Card
                      sx={{
                        maxWidth: 400,
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'transform 0.3s',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          cursor: 'pointer',
                        },
                        position: 'relative',
                      }}
                      onClick={() => handleVideoClick(item.url)}
                    >
                      {/* ───── Thumbnail ───── */}
                      {(thumbnailLoading && selectedItem.id === item.id) ? (
                        <Box
                          sx={{
                            height: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.100',
                          }}
                        >
                          <CircularProgress size={40} thickness={4} />
                        </Box>
                      ) :
                        item.thumbUrl ? (
                          <Box sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              image={item.thumbUrl}
                              alt={item.displayName || item.name}
                              sx={{
                                width: '100%',
                                height: 200,
                                objectFit: 'fill',
                              }}
                              onError={(e) => {
                                e.currentTarget.src = '';
                              }}
                            />
                            {/* ─────── Duration Overlay ─────── */}
                            {item.duration && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: '#fff',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                {formatDuration(item.duration)}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.200',
                            }}
                          >
                            <PlayCircleFilledRounded fontSize="large" color="disabled" />
                            <Typography ml={1} color="text.secondary">
                              No Thumbnail
                            </Typography>
                          </Box>
                        )}
                    </Card>


                    {/* ───── Title + Menu ───── */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          padding: '9px 0 4px 0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: '#000',
                          width: '85%',
                        }}
                      >
                        {item.displayName || item.name}
                      </Typography>

                      {/* More Menu */}
                      <IconButton
                        size="small"
                        sx={{
                          lineHeight: 2
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, item);
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* ───── Date (optional) ───── */}
                    {item.createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatRelativeDate(item.createdAt)}
                      </Typography>
                    )}

                  </Grid>
                ))}

                {/* ───── Context Menu ───── */}
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      const newName = prompt("Enter new name", selectedItem.name);
                      if (newName) handleRename(selectedItem, newName);
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      openAssignDialog(selectedItem);
                    }}
                  >
                    Assign
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      handleDelete(selectedItem);
                    }}
                  >
                    Delete
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      setSelectedItem(selectedItem);
                      setThumbnailModalOpen(true);
                    }}
                  >
                    Upload Thumbnail
                  </MenuItem>
                </Menu>
              </Grid>

              {/* Upload progress bar */}
              <ProgressBar progress={progress} />
            </>}


          <Dialog open={openFolderDialog} onClose={() => setOpenFolderDialog(false)}>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogContent>
              <TextField
                label="Folder Name"
                fullWidth
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                sx={{ mt: 0.7 }}
              />
              <Button onClick={createFolder} variant="contained" sx={{ mt: 2 }}>
                Create
              </Button>
            </DialogContent>
          </Dialog>

          <VideoDialog
            open={openVideoDialog}
            onClose={() => setOpenVideoDialog(false)}
            videoUrl={selectedVideoUrl}
          />

          {/* Confirm Delete Dialog */}
          <ConfirmDialog
            open={confirmOpen}
            title="Confirm Deletion"
            content={`Are you sure? Do you want to delete this ${capitalizeFirstLetter(deletedItem?.type)}?`}
            onClose={handleConfirmClose}
            onConfirm={confirmDeleteVideo}
          />

          {/* Assign to Students Dialog */}
          <Dialog
            open={assignDialogOpen}
            onClose={handleAssignDialogClose}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              Assign {assignItem?.type === "folder" ? "Folder" : "Video"}: {assignItem?.name}
            </DialogTitle>
            <DialogContent dividers>
              {assignLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {students.length === 0 ? (
                    <Typography>No students available</Typography>
                  ) : (
                    students.map(student => (
                      <ListItem key={student.id} dense button onClick={() => handleAssignCheckboxChange(student.id)}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={assignedStudentIds.has(student.id)}
                              onChange={() => handleAssignCheckboxChange(student.id)}
                            />
                          }
                          label={student.name}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              )}
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={handleAssignDialogClose} disabled={assigning}>Cancel</Button>
                <Button variant="contained" onClick={handleAssignConfirm} disabled={assigning}>
                  {assigning ? "Saving..." : "Save"}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          <ThumbnailUploadModal
            open={thumbnailModalOpen}
            onClose={() => setThumbnailModalOpen(false)}
            onUpload={ThumbnailUploader}
            item={selectedItem}
            currentPath={currentPath}
            setRefresh={setRefresh}
            setThumbnailLoading={setThumbnailLoading}
          />
        </Box>}
    </>
  );
};

export default VideosDashboard;
